import { defineStore } from "pinia";
import { clubService } from "@/club/services/clubService";
import { ClubInfoView } from "@/club/types/club";

import { getLogger } from "@/utils/logger";
import { toApiError } from "@/core/api/ApiError";

const logger = getLogger("ClubStore");

interface ClubState {
  activeClub: {
    shortName: string;
    clubName: string;
    clubId: number;
    loading: boolean;
    error: string | null;
    clubData: ClubInfoView | null;
  };
}

export const useClubStore = defineStore("club", {
  state: (): ClubState => ({
    activeClub: {
      shortName: "",
      clubName: "",
      clubId: 0,
      loading: false,
      error: null,
      clubData: null,
    },
  }),
  getters: {
    getClubName(): string {
      return this.activeClub.clubName;
    },
    getClubId(): number {
      return this.activeClub.clubId;
    },
    getClubShortName(): string {
      return this.activeClub.shortName;
    },
    isLoading(): boolean {
      return this.activeClub.loading;
    },
    getError(): string | null {
      return this.activeClub.error;
    },
    getClubData(): ClubInfoView | null {
      return this.activeClub.clubData;
    },
  },
  actions: {
    /**
     * Load the club identified by `clubShortName` (accepts the real short name or
     * the `c<id>` route alias) and make it the active club.
     *
     * `clubId` and `clubData` describe `shortName` at all times, never a
     * previously-visited club (#2671). Callers across the app treat a truthy
     * `getClubId` as "the club in the URL is loaded" and read or write against it,
     * so leaving the old club's id in place while a new one resolved let screens
     * show — and save to — the wrong club.
     */
    async loadClubData(clubShortName: string) {
      // If club is already loaded, don't reload
      if (this.activeClub.shortName === clubShortName && this.activeClub.clubData) {
        return;
      }

      // Switching clubs: drop the previous club's identity before going to the
      // network, so consumers see "not loaded" rather than the old club.
      if (this.activeClub.shortName !== clubShortName) {
        this.activeClub.clubId = 0;
        this.activeClub.clubName = "";
        this.activeClub.clubData = null;
      }

      this.activeClub.shortName = clubShortName;
      this.activeClub.loading = true;
      this.activeClub.error = null;

      try {
        const clubResponse = await clubService.getClubByShortName(clubShortName);
        // A newer request for a different club may have started while this one was
        // in flight; the last request to be issued owns the state, not the last to
        // return.
        if (this.activeClub.shortName !== clubShortName) return;
        this.activeClub.clubName = clubResponse.publicInfo.longName;
        this.activeClub.clubId = clubResponse.publicInfo.clubId;
        this.activeClub.clubData = clubResponse;
      } catch (error: unknown) {
        if (this.activeClub.shortName !== clubShortName) return;
        logger.error("Error loading club data:", error);
        // The fallback rides `clubService.getClubByShortName`'s `runGraphQL`
        // call; a second one here would be dead (`toApiError` is idempotent).
        this.activeClub.error = toApiError(error).message;
      } finally {
        if (this.activeClub.shortName === clubShortName) {
          this.activeClub.loading = false;
        }
      }
    },
    clearClubData() {
      this.activeClub = {
        shortName: "",
        clubName: "",
        clubId: 0,
        loading: false,
        error: null,
        clubData: null,
      };
    },
  },
});
